/**
 * store.ts — Redux store factory for @hostesa/state.
 *
 * createAppStore() combines all UI slices and configures:
 * - Redux DevTools (enabled in development; disabled in production builds)
 * - SerializableCheck middleware configured to ignore known non-serializable paths
 * - DevTools state sanitizer: strips fields that could contain sensitive display names
 *   from DevTools snapshots to reduce the risk of inadvertent PII exposure in
 *   operator tooling. Note: impersonatedUserLabel is a display string only (never
 *   email/phone), but we sanitize it defensively per the no-PII-in-DevTools rule.
 *
 * Exported types:
 *   RootState   — inferred from the store reducer map
 *   AppDispatch — typed dispatch for use with useAppDispatch()
 */
import { type ConfigureStoreOptions, combineReducers, configureStore } from '@reduxjs/toolkit';
import { commandPaletteSlice } from './slices/commandPaletteSlice';
import { impersonationSlice } from './slices/impersonationSlice';
import { notificationsSlice } from './slices/notificationsSlice';
import { uiSlice } from './slices/uiSlice';
import { workflowSlice } from './slices/workflowSlice';

const rootReducer = combineReducers({
  [uiSlice.name]: uiSlice.reducer,
  [commandPaletteSlice.name]: commandPaletteSlice.reducer,
  [impersonationSlice.name]: impersonationSlice.reducer,
  [workflowSlice.name]: workflowSlice.reducer,
  [notificationsSlice.name]: notificationsSlice.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

/**
 * DevTools state sanitizer.
 *
 * Strips the impersonatedUserLabel from DevTools snapshots so operator tooling
 * never persists the display string of the impersonated context in DevTools
 * recordings or state exports. All other state is non-PII by design.
 *
 * @see packages/state README §PII contract
 */
function devToolsStateSanitizer(state: unknown): unknown {
  const s = state as RootState;
  if (s.impersonation?.active) {
    return {
      ...s,
      impersonation: {
        ...s.impersonation,
        // Replace label with a placeholder in DevTools only; reducer state is unaffected.
        impersonatedUserLabel: '[REDACTED for DevTools]',
      },
    };
  }
  return state;
}

export function createAppStore(
  preloadedState?: ConfigureStoreOptions<RootState>['preloadedState'],
) {
  return configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // No non-serializable values expected in this slice set.
          // If future slices add Date objects, add ignoredPaths here rather than
          // disabling the check globally.
          ignoredActions: [],
          ignoredPaths: [],
        },
      }),
    devTools: (process.env.NODE_ENV !== 'production'
      ? { name: 'Hostesa App', stateSanitizer: devToolsStateSanitizer }
      : false) as ConfigureStoreOptions<RootState>['devTools'],
  });
}

export type AppStore = ReturnType<typeof createAppStore>;
export type AppDispatch = AppStore['dispatch'];
